import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import ChatArea from './ChatArea';
import Modals from './Modals';
import { fetchChats, fetchChatMessages, fetchModels, fetchUser } from '../api';
import { ChatGroup, Message, AIModel, User, Chat } from '../types';
import styles from './ChatApp.module.css';
import { Helmet } from 'react-helmet';

const ChatApp: React.FC = () => {
    const { uuid } = useParams<{ uuid?: string }>();
    const navigate = useNavigate();
    const [chats, setChats] = useState<ChatGroup[]>([]);
    const [activeChat, setActiveChat] = useState<Chat | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [models, setModels] = useState<AIModel[]>([]);
    const [user, setUser] = useState<User | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSidebarHidden, setIsSidebarHidden] = useState(false);
    const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false);
    const [balance, setBalance] = useState<number>(0);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [chatData, modelData, userData] = await Promise.all([
                    fetchChats(),
                    fetchModels(),
                    fetchUser(),
                ]);
                setChats(chatData);
                setModels(modelData);
                setUser(userData);
                setBalance(userData.balance);

                if (uuid) {
                    const chat = chatData.flatMap((g) => g.chats).find((c) => c.uuid === uuid);
                    if (chat) {
                        setActiveChat(chat);
                        const chatMessages = await fetchChatMessages(uuid);
                        setMessages(chatMessages);
                    } else {
                        navigate('/chat/');
                    }
                } else {
                    setActiveChat(null);
                    setMessages([]);
                }
            } catch (error) {
                console.error('Ошибка загрузки данных:', error);
            }
        };
        loadInitialData();
    }, [uuid, navigate]);

    const toggleSidebar = () => {
        setIsSidebarHidden((prev) => !prev);
    };

    const handleBalanceUpdate = (newBalance: number) => {
        setBalance(newBalance);
    };

    const updateScrollDownButtonPosition = useCallback(() => {
        const scrollDownBtn = document.getElementById('scrollDownBtn') as HTMLButtonElement;
        const chatWidth = 768;
        const sidebarWidth = 280;

        if (scrollDownBtn) {
            if (window.innerWidth > 768) {
                const rightOffset = isSidebarHidden
                    ? (window.innerWidth - chatWidth) / 2 - 60
                    : (window.innerWidth - chatWidth - sidebarWidth) / 2 - 60;
                scrollDownBtn.style.right = `${rightOffset}px`;
            } else {
                scrollDownBtn.style.right = '20px';
            }
        }
    }, [isSidebarHidden]);

    useEffect(() => {
        updateScrollDownButtonPosition();
        window.addEventListener('resize', updateScrollDownButtonPosition);
        return () => window.removeEventListener('resize', updateScrollDownButtonPosition);
    }, [isSidebarHidden, updateScrollDownButtonPosition]);

    return (
        <>
            <Helmet>
                <meta charSet="UTF-8" />
                <title>Чат GPT4Chats - Ваш ИИ-помощник в действии</title>
                <meta
                    name="description"
                    content="Используйте чат GPT4Chats для общения с ИИ на базе GPT-4. Генерируйте тексты, анализируйте документы и решайте задачи в реальном времени."
                />
                <meta
                    name="keywords"
                    content="чат GPT4Chats, ИИ-помощник, GPT-4, генерация текста, анализ документов, искусственный интеллект"
                />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href={`https://gpt4chats.ru/chat${uuid ? `/${uuid}` : ''}`} />
                <meta property="og:title" content="Чат GPT4Chats - Ваш ИИ-помощник в действии" />
                <meta
                    property="og:description"
                    content="Общайтесь с ИИ в чате GPT4Chats: генерация текстов, анализ документов и помощь в задачах с использованием GPT-4."
                />
                <meta property="og:url" content={`https://gpt4chats.ru/chat${uuid ? `/${uuid}` : ''}`} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="GPT4Chats" />
            </Helmet>
            <div className={styles.container}>
                <aside className={`${styles.sidebar} ${isSidebarHidden ? styles.sidebarHidden : ''}`}>
                    <Sidebar
                        chats={chats}
                        toggleSidebar={toggleSidebar}
                        setActiveChat={(chat: Chat) => {
                            setActiveChat(chat);
                            navigate(`/chat/${chat.uuid}`);
                        }}
                        setChats={setChats}
                        activeChatUuid={uuid}
                    />
                </aside>
                <main className={styles.mainContent}>
                    <Header
                        toggleSidebar={toggleSidebar}
                        activeChat={activeChat}
                        isSidebarHidden={isSidebarHidden}
                        balance={balance}
                        setBalance={setBalance}
                    />
                    <ChatArea
                        messages={messages}
                        setMessages={setMessages}
                        activeChatUuid={uuid}
                        isGenerating={isGenerating}
                        setIsGenerating={setIsGenerating}
                        currentModel={user?.current_model || 'GPT-4'}
                        setIsScrollButtonVisible={setIsScrollButtonVisible}
                        onBalanceUpdate={handleBalanceUpdate}
                    />
                </main>
                <Modals />
                <div id="chatMenu" className={styles.chatOptionsPopup} style={{ display: 'none' }}></div>
            </div>
        </>
    );
};

export default ChatApp;